import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth as authApi } from '../utils/api';

const AuthContext = createContext(null);

// AIstotele-SEC-001 — guardia idempotenza per il consumo one-shot #exchange.
// React StrictMode (dev) invoca il mount effect DUE volte: senza guardia la
// seconda invocazione legge l'hash gia' ripulito da replaceState, non trova
// token e fa setLoading(false) → redirect a /auth/login PRIMA che lo scambio
// di rete si risolva (impersonation rotta in `npm start`). Il flag, settato
// in modo sincrono nella prima invocazione, rende la seconda un no-op finche'
// lo scambio non si conclude. Si resetta SEMPRE nel finally (successo O
// errore): non resta mai "sporco", quindi dopo un logout una nuova
// impersonation nella stessa sessione browser riparte pulita. Vive solo sul
// path #exchange — indipendente dal path legacy rimosso in PR3.
let impersonationExchangeInFlight = false;

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [features, setFeatures] = useState({});
  const [piano, setPiano]     = useState('free');
  const [loading, setLoading] = useState(true);

  // Al mount: verifica token esistente o impersonation URL params
  // access_token in sessionStorage; se assente ma refresh_token presente, auto-refresh via api.js
  useEffect(() => {
    // Guardia StrictMode: se uno scambio #exchange e' gia' in volo, la seconda
    // invocazione dell'effect (dev double-invoke) e' no-op e non tocca `loading`.
    if (impersonationExchangeInFlight) return;

    const hash = window.location.hash || '';
    const hashParams = hash.startsWith('#')
      ? new URLSearchParams(hash.slice(1))
      : new URLSearchParams();

    // AIstotele-SEC-001 — flusso one-shot: #exchange=<uuid>. Il JWT non viaggia
    // mai in URL: scambiamo l'UUID monouso col JWT (nel body) via POST. I
    // metadati banner arrivano dal BODY della response, non dall'URL.
    const exchangeUuid = hashParams.get('exchange');
    if (exchangeUuid) {
      // Marca lo scambio come in volo PRIMA di qualsiasi async/await: la
      // seconda invocazione dell'effect (StrictMode) vedra' il flag e fara'
      // early-return (vedi guardia in cima all'effect).
      impersonationExchangeInFlight = true;
      // Pulisci subito hash/URL PRIMA di qualsiasi await: l'UUID non deve
      // restare in window.location.hash ne' in browser history durante
      // l'attesa di rete della POST. Usiamo la variabile gia' in memoria.
      window.history.replaceState({}, '', window.location.pathname);
      (async () => {
        try {
          const { data } = await authApi.exchangeImpersonation(exchangeUuid);
          sessionStorage.setItem('access_token', data.access);
          if (data.impersonated_by) sessionStorage.setItem('impersonated_by', data.impersonated_by);
          if (data.impersonated_user) sessionStorage.setItem('impersonated_user', data.impersonated_user);
          loadMe();
        } catch {
          // 404 / throttle / network: niente mezzo stato. URL gia' pulito sopra,
          // resta sloggato, errore chiaro via toast globale (listener 'api-error').
          window.dispatchEvent(new CustomEvent('api-error', {
            detail: { message: 'Sessione di impersonation non valida o scaduta.' },
          }));
          setLoading(false);
        } finally {
          // Reset SEMPRE (successo o errore): il flag non resta mai sporco.
          impersonationExchangeInFlight = false;
        }
      })();
      return;
    }

    const token = sessionStorage.getItem('access_token');
    const refresh = localStorage.getItem('refresh_token');
    if (token || refresh) {
      loadMe();
    } else {
      setLoading(false);
    }
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const [meRes, featRes] = await Promise.all([
        authApi.me(),
        authApi.features(),
      ]);
      setUser(meRes.data);
      setFeatures(featRes.data.flags);
      setPiano(featRes.data.piano);
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const { data } = await authApi.login(email, password);
    // MFA: se il backend richiede verifica magic link, non emette JWT
    if (data.mfa_required) {
      return { mfa_required: true, email: data.email };
    }
    sessionStorage.setItem('access_token', data.access);
    localStorage.setItem('refresh_token', data.refresh);
    // Carica dati utente e feature flags con i token appena ottenuti
    const [meRes, featRes] = await Promise.all([
      authApi.me(),
      authApi.features(),
    ]);
    setUser(meRes.data);
    setFeatures(featRes.data.flags);
    setPiano(featRes.data.piano);
    return meRes.data;
  };

  const verifyMfa = async (token) => {
    const { data } = await authApi.verifyMagicLink(token);
    sessionStorage.setItem('access_token', data.access);
    localStorage.setItem('refresh_token', data.refresh);
    const [meRes, featRes] = await Promise.all([
      authApi.me(),
      authApi.features(),
    ]);
    setUser(meRes.data);
    setFeatures(featRes.data.flags);
    setPiano(featRes.data.piano);
    return meRes.data;
  };

  const socialLogin = async (provider, code, redirect_uri, code_verifier) => {
    const { data } = await authApi.socialLogin(provider, code, redirect_uri, code_verifier);
    sessionStorage.setItem('access_token', data.access);
    localStorage.setItem('refresh_token', data.refresh);
    const [meRes, featRes] = await Promise.all([
      authApi.me(),
      authApi.features(),
    ]);
    setUser(meRes.data);
    setFeatures(featRes.data.flags);
    setPiano(featRes.data.piano);
    return meRes.data;
  };

  // Cantiere Registrazione, Fetta 2b (DEC-064): completa la registrazione
  // self-service. Stesso contratto di socialLogin (salva JWT, carica utente e
  // feature flag), ma l'ingresso e' il form di adesione col biglietto firmato.
  // Volutamente "entry-agnostic": accetta gia' l'intero payload del form, cosi'
  // la registrazione email/password (Fetta 5) potra' riusarla senza modifiche.
  const completaRegistrazione = async (datiForm) => {
    const { data } = await authApi.registerComplete(datiForm);
    sessionStorage.setItem('access_token', data.access);
    localStorage.setItem('refresh_token', data.refresh);
    const [meRes, featRes] = await Promise.all([
      authApi.me(),
      authApi.features(),
    ]);
    setUser(meRes.data);
    setFeatures(featRes.data.flags);
    setPiano(featRes.data.piano);
    return meRes.data;
  };

  const logout = () => {
    // FE-021 — blacklista il refresh token lato server (best-effort,
    // non blocca il logout locale anche se la chiamata fallisce).
    const refresh = localStorage.getItem('refresh_token');
    if (refresh) {
      authApi.logout(refresh).catch(() => {});
    }
    sessionStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    // SEC-003 — pulizia simmetrica: l'impersonation salva anche questi claim
    // (vedi blocco impersonation al mount). Senza rimuoverli, il banner
    // resterebbe attivo su una sessione successiva non impersonata.
    sessionStorage.removeItem('impersonated_by');
    sessionStorage.removeItem('impersonated_user');
    setUser(null);
    setFeatures({});
    setPiano('free');
  };

  // Helper: controlla se una feature è attiva
  const hasFeature = (flagName) => features[flagName] === true;

  return (
    <AuthContext.Provider value={{
      user, features, piano, loading,
      login, socialLogin, verifyMfa, completaRegistrazione, logout, loadMe, hasFeature
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve essere usato dentro AuthProvider');
  return ctx;
};
